import { ConnectError } from "@connectrpc/connect";
import { API_URL } from "./env";

/**
 * Tells the api about an error the app caught while sending, so a failure that never reaches a
 * handler still leaves a line on the server (api `/client-report`). Written on 2026-11-01, when a
 * new member could not send from Firefox and nothing on the server said why.
 *
 * Carries the error's name, code, text and stack, the build and the screen without its query.
 * Never the message being sent. Fire and forget: reporting must not fail a send twice.
 */
export function reportClientError(kind: string, err: unknown): void {
  try {
    const e = err instanceof Error ? err : new Error(String(err));
    const body = JSON.stringify({
      kind,
      name: e.name,
      code: err instanceof ConnectError ? String(err.code) : "",
      message: err instanceof ConnectError ? err.rawMessage : e.message,
      stack: e.stack ?? "",
      build: typeof __BUILD_SHA__ === "string" ? __BUILD_SHA__ : "",
      route: typeof location === "undefined" ? "" : location.pathname,
      client: "web",
    });
    // text/plain keeps it a simple request: no preflight that could itself be what is failing.
    void fetch(`${API_URL}/client-report`, {
      method: "POST",
      body,
      headers: { "content-type": "text/plain" },
      credentials: "omit",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // Nothing to do: the report is a courtesy.
  }
}

/** A failed send as the SDK keeps it: "[code] reason". */
export function reportSendFailed(error: string): void {
  const m = error.match(/^\[([a-z_]+)\]\s*(.*)$/s);
  const e = new Error(m ? (m[2] ?? "") : error);
  e.name = m ? `ConnectError:${m[1]}` : "SendFailed";
  e.stack = "";
  reportClientError("send-failed", e);
}
